import { useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
    StyledDialog,
    StyledDialogContainer
} from '@/components/consent/StyledDialog';
import {
    OutlinedSecondaryButtonLG,
    PrimaryButtonLG
} from '@/components/shared/Button';
import { InputText } from '@/components/shared/InputText';
import { IOSSwitch } from '@/components/shared/Switch';
import { SMBold, SMRegular, SMSemiBold } from '@/typography/Typography';
import { staticColors } from '@/lib/theme';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useGetHouseAccountBankMaterial } from '@/services/paymentsQuery';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useErrorHandling } from '@/utils/errorHandling';
import { InstaPaidHouseAccount } from '@/services/PaymentsServiceV2';
import {
    isValidBic,
    isValidCvr,
    isValidIban,
    normaliseBic,
    normaliseCvr,
    normaliseIban
} from '@/utils/bankMaterialValidation';
import { BankDetailsFields } from './BankDetailsFields';

interface EditHouseAccountDialogProps {
    // Null closes the dialog; a non-null account opens it pre-filled with that
    // account's current Name/IsActive. The four bank-detail inputs are pre-filled
    // with the account's real current values (this is a Global-Admin-only surface)
    // once they are fetched, so the admin can correct them in place.
    account: InstaPaidHouseAccount | null;
    onClose: () => void;
}

interface BankFieldValues {
    accountHolderName: string;
    cvr: string;
    iban: string;
    bic: string;
}

const EMPTY_BANK_FIELDS: BankFieldValues = {
    accountHolderName: '',
    cvr: '',
    iban: '',
    bic: ''
};

export const EditHouseAccountDialog = ({
    account,
    onClose
}: EditHouseAccountDialogProps) => {
    const { t } = useTranslation();
    const { setAlert } = useNotificationsSnackbar();
    const { backendErrorHandler } = useErrorHandling();
    const { updateHouseAccount } = usePaymentsMutations();

    const [name, setName] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [accountHolderName, setAccountHolderName] = useState('');
    const [cvr, setCvr] = useState('');
    const [iban, setIban] = useState('');
    const [bic, setBic] = useState('');
    const [nameTaken, setNameTaken] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const {
        data: bankMaterialResponse,
        isLoading: isBankMaterialLoading,
        isError: isBankMaterialError
    } = useGetHouseAccountBankMaterial(
        account?.instaPaidHouseAccountId,
        !!account
    );

    // The real values the four fields were last seeded with, kept so dirty
    // tracking compares edits against the actual stored value (not against
    // blank). seededAccountIdRef guards the one-time seed: it flips to the open
    // account's id after seeding so a background react-query refetch of the same
    // account never stomps on values the admin is actively editing.
    const originalBankFieldsRef = useRef<BankFieldValues>(EMPTY_BANK_FIELDS);
    const seededAccountIdRef = useRef<string | null>(null);

    // Opening (or switching to) an account resets synchronously from the account
    // prop; the bank fields go blank and stay blank until their fetch resolves,
    // so the previous account's values never linger during the new load.
    useEffect(() => {
        setName(account?.name ?? '');
        setIsActive(account?.isActive ?? true);
        setAccountHolderName('');
        setCvr('');
        setIban('');
        setBic('');
        setNameTaken(false);
        setSubmitAttempted(false);
        originalBankFieldsRef.current = EMPTY_BANK_FIELDS;
        seededAccountIdRef.current = null;
    }, [account]);

    // Seed the editable fields from the fetched values exactly once per open
    // account, the moment the fetch settles (success or error). On error the
    // seed is blank so the admin can still type new values from scratch.
    useEffect(() => {
        if (!account || isBankMaterialLoading) {
            return;
        }
        if (seededAccountIdRef.current === account.instaPaidHouseAccountId) {
            return;
        }

        const material = bankMaterialResponse?.data;
        const seeded: BankFieldValues = material?.hasBankMaterial
            ? {
                  accountHolderName: material.accountHolderName ?? '',
                  cvr: material.cvr ?? '',
                  iban: material.iban ?? '',
                  bic: material.bic ?? ''
              }
            : EMPTY_BANK_FIELDS;

        setAccountHolderName(seeded.accountHolderName);
        setCvr(seeded.cvr);
        setIban(seeded.iban);
        setBic(seeded.bic);
        originalBankFieldsRef.current = seeded;
        seededAccountIdRef.current = account.instaPaidHouseAccountId;
    }, [account, isBankMaterialLoading, bankMaterialResponse]);

    const resetAndClose = () => {
        setName('');
        setAccountHolderName('');
        setCvr('');
        setIban('');
        setBic('');
        setNameTaken(false);
        setSubmitAttempted(false);
        onClose();
    };

    // An already-provisioned account accepts ANY subset of the four fields — the fields arrive pre-filled with
    // their real values, so leaving one unchanged keeps it, and editing one replaces just that one. A
    // not-yet-provisioned account has no existing value for an omitted field to fall back to, so it still
    // requires all four together the first time (mirrors CreateHouseAccountDialog and the backend's
    // BankMaterialIncompleteForFirstProvisioning).
    const isProvisioned = account?.isPayoutCapable ?? false;

    const original = originalBankFieldsRef.current;

    const holderNameEntered = accountHolderName.trim().length > 0;
    const cvrEntered = cvr.trim().length > 0;
    const ibanEntered = iban.trim().length > 0;
    const bicEntered = bic.trim().length > 0;

    // A field counts as edited only when it holds content that differs from the value it was seeded with.
    // Clearing a pre-filled field back to empty is not treated as an edit — there is no "unset bank material"
    // operation on this surface, so an emptied field is simply omitted from the payload.
    const holderNameChanged =
        holderNameEntered &&
        accountHolderName.trim() !== original.accountHolderName.trim();
    const cvrChanged =
        cvrEntered && normaliseCvr(cvr) !== normaliseCvr(original.cvr);
    const ibanChanged =
        ibanEntered && normaliseIban(iban) !== normaliseIban(original.iban);
    const bicChanged =
        bicEntered && normaliseBic(bic) !== normaliseBic(original.bic);
    const bankFieldsChanged =
        holderNameChanged || cvrChanged || ibanChanged || bicChanged;

    const requiresAllFour = !isProvisioned && bankFieldsChanged;

    // Format validity only applies to a field that actually has content — an empty field is never itself a
    // format error. A field left blank while requiresAllFour is a DIFFERENT problem (missing, not malformed),
    // so each field's overall validity also fails when it's the one missing from a required complete set.
    const cvrValid = (!cvrEntered || isValidCvr(cvr.trim())) && !(requiresAllFour && !cvrEntered);
    const ibanValid = (!ibanEntered || isValidIban(iban)) && !(requiresAllFour && !ibanEntered);
    const bicValid = (!bicEntered || isValidBic(bic)) && !(requiresAllFour && !bicEntered);
    const holderNameValid = !requiresAllFour || holderNameEntered;
    const allFourPresentIfRequired =
        !requiresAllFour ||
        (holderNameEntered && cvrEntered && ibanEntered && bicEntered);

    const bankDetailsValid =
        cvrValid &&
        ibanValid &&
        bicValid &&
        holderNameValid &&
        allFourPresentIfRequired;

    const hasChanges =
        !!account &&
        (name.trim() !== account.name ||
            isActive !== account.isActive ||
            bankFieldsChanged);

    // Full bank-detail validity is deliberately NOT required here — mirrors
    // CreateHouseAccountDialog: Save stays clickable once there is a pending
    // change, and handleSave itself blocks the submit and reveals the inline
    // field errors below rather than leaving the operator stuck on a disabled
    // button with no explanation.
    const canSave =
        name.trim().length > 0 && hasChanges && !updateHouseAccount.isPending;

    const handleSave = () => {
        setSubmitAttempted(true);
        if (!account || !bankDetailsValid) {
            return;
        }

        updateHouseAccount.mutate(
            {
                instaPaidHouseAccountId: account.instaPaidHouseAccountId,
                name: name.trim(),
                isActive,
                // Only fields the admin actually changed from their seeded value are sent — an untouched
                // pre-filled field is omitted so the backend leaves its stored secret as-is.
                ...(holderNameChanged && {
                    accountHolderName: accountHolderName.trim()
                }),
                ...(cvrChanged && { cvr: normaliseCvr(cvr) }),
                ...(ibanChanged && { iban: normaliseIban(iban) }),
                ...(bicChanged && { bic: normaliseBic(bic) })
            },
            {
                onSuccess: () => {
                    setAlert(t('house_accounts.rename_success'), 'success');
                    resetAndClose();
                },
                onError: (error) => {
                    const status = (error as AxiosError)?.response?.status;
                    // Duplicate name is a friendly inline error on the field;
                    // the dialog stays open so the operator can rename and retry.
                    if (status === 409) {
                        setNameTaken(true);
                        return;
                    }
                    // Secret-write failure: no field was changed server-side,
                    // so the operator can simply retry. Keep the dialog open.
                    if (status === 502) {
                        setAlert(
                            t('house_accounts.secret_write_failed_error'),
                            'error'
                        );
                        return;
                    }
                    backendErrorHandler()(error);
                }
            }
        );
    };

    return (
        <StyledDialog open={!!account} onClose={resetAndClose}>
            <StyledDialogContainer style={{ width: '440px', padding: '24px' }}>
                <SMSemiBold mb={2}>
                    {t('house_accounts.edit_account')}
                </SMSemiBold>

                <InputText
                    testid="house-account-edit-name"
                    label={t('house_accounts.name')}
                    required
                    value={name}
                    onChange={(event) => {
                        setName(event.target.value);
                        if (nameTaken) {
                            setNameTaken(false);
                        }
                    }}
                    error={nameTaken}
                    helperText={
                        nameTaken
                            ? t('house_accounts.name_taken_error')
                            : undefined
                    }
                />

                <Box
                    mt={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <SMSemiBold>{t('house_accounts.active_toggle_label')}</SMSemiBold>
                    <IOSSwitch
                        checked={isActive}
                        onChange={(_event, checked) => setIsActive(checked)}
                    />
                </Box>

                <Box mt={3}>
                    <SMSemiBold>
                        {t('house_accounts.bank_details_section')}
                    </SMSemiBold>

                    <SMRegular mt={0.5} mb={1.5}>
                        {t(
                            isProvisioned
                                ? 'house_accounts.bank_details_section_hint_partial'
                                : 'house_accounts.bank_details_section_hint'
                        )}
                    </SMRegular>

                    {isBankMaterialError && (
                        <SMRegular
                            mb={1.5}
                            color={staticColors.error}
                        >
                            {t('house_accounts.bank_details_load_error')}
                        </SMRegular>
                    )}

                    <BankDetailsFields
                        accountHolderName={accountHolderName}
                        onAccountHolderNameChange={setAccountHolderName}
                        cvr={cvr}
                        onCvrChange={setCvr}
                        iban={iban}
                        onIbanChange={setIban}
                        bic={bic}
                        onBicChange={setBic}
                        disabled={isBankMaterialLoading}
                        holderNameError={submitAttempted && !holderNameValid}
                        cvrError={submitAttempted && !cvrValid}
                        ibanError={submitAttempted && !ibanValid}
                        bicError={submitAttempted && !bicValid}
                    />
                </Box>

                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    gap={1.5}
                    mt={3}
                >
                    <OutlinedSecondaryButtonLG
                        color="secondary"
                        style={{ height: '40px', borderRadius: '8px' }}
                        onClick={resetAndClose}
                    >
                        <SMBold textTransform="none">{t('Cancel')}</SMBold>
                    </OutlinedSecondaryButtonLG>
                    <PrimaryButtonLG
                        style={{ height: '40px', borderRadius: '8px' }}
                        disabled={!canSave}
                        onClick={handleSave}
                    >
                        <SMBold textTransform="none">{t('save')}</SMBold>
                    </PrimaryButtonLG>
                </Stack>
            </StyledDialogContainer>
        </StyledDialog>
    );
};

export default EditHouseAccountDialog;
